/**
 * Утилита для выполнения авторизованных API запросов
 * Автоматически добавляет токен авторизации из localStorage
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Получить токен из localStorage
 */
const getToken = (): string | null => {
  return localStorage.getItem('token');
};

/**
 * Выполнить API запрос с автоматическим добавлением токена
 */
export const apiRequest = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = getToken();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Добавляем токен если он есть
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers,
    // Для ERP API всегда берем свежие данные:
    // 304 без тела ломает response.json() в apiGet.
    cache: 'no-store',
  });

  // Если получили 429 (Too Many Requests), добавляем информацию о задержке
  if (response.status === 429) {
    const retryAfter = response.headers.get('Retry-After');
    const retryAfterSeconds = retryAfter ? parseInt(retryAfter, 10) : 60;
    console.warn(`[API] Rate limit exceeded. Retry after ${retryAfterSeconds} seconds`);
    // Не бросаем ошибку сразу, чтобы не прерывать работу приложения
    // Просто логируем предупреждение
  }

  // Если получили 401 или 403, значит токен истек или невалиден
  if (response.status === 401 || response.status === 403) {
    // Очищаем токен и перенаправляем на страницу входа (только если не на странице логина и не на публичной странице экспорта)
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      const isLoginPage = currentPath.includes('/login');
      const isExportPage = currentPath.includes('/standings-export');
      
      console.log('[API] 401/403 ошибка, текущий путь:', currentPath, 'isLoginPage:', isLoginPage, 'isExportPage:', isExportPage);
      
      if (!isLoginPage && !isExportPage) {
        console.log('[API] Перенаправление на /login');
        window.location.href = '/login';
      } else {
        console.log('[API] Пропуск перенаправления (публичная страница)');
      }
    }
    
    throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
  }

  return response;
};

/**
 * GET запрос
 */
export const apiGet = async <T = any>(
  endpoint: string,
  params?: Record<string, string | number | boolean>
): Promise<T> => {
  const url = params
    ? `${endpoint}?${new URLSearchParams(params as Record<string, string>).toString()}`
    : endpoint;
  const response = await apiRequest(url, { method: 'GET' });
  if (!response.ok) {
    const retryAfterHeader = response.headers.get('Retry-After');
    const retryAfterSeconds = retryAfterHeader ? Number.parseInt(retryAfterHeader, 10) : 0;
    const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    // Для ошибок 500 на /auth/profile (проверка авторизации) не логируем в консоль
    const isAuthCheck = endpoint.includes('/auth/profile');
    if (!isAuthCheck || response.status !== 500) {
      console.error(`API Error [${response.status}]:`, error);
    }
    const apiError: any = new Error(error.error || `HTTP ${response.status}`);
    apiError.status = response.status;
    if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
      apiError.retryAfterSeconds = retryAfterSeconds;
    }
    throw apiError;
  }
  return response.json();
};

/**
 * POST запрос
 */
export const apiPost = async <T = any>(
  endpoint: string,
  data?: any
): Promise<T> => {
  // DEBUG логи только в development (используем import.meta.env.DEV для Vite)
  if (import.meta.env.DEV) {
    console.log(`[DEBUG API] POST ${endpoint}`, data);
  }
  try {
    const response = await apiRequest(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
    if (import.meta.env.DEV) {
      console.log(`[DEBUG API] POST ${endpoint} response status:`, response.status);
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Ошибка запроса' }));
      console.error(`[DEBUG API] POST ${endpoint} error:`, errorData);
      const error: any = new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      error.response = { data: errorData, status: response.status, statusText: response.statusText };
      throw error;
    }
    const result = await response.json();
    if (import.meta.env.DEV) {
      console.log(`[DEBUG API] POST ${endpoint} success:`, result);
    }
    return result;
  } catch (error) {
    // Ошибки всегда логируем
    console.error(`[DEBUG API] POST ${endpoint} exception:`, error);
    throw error;
  }
};

/**
 * PUT запрос
 */
export const apiPut = async <T = any>(
  endpoint: string,
  data?: any
): Promise<T> => {
  const response = await apiRequest(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Ошибка запроса' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
};

/**
 * DELETE запрос
 */
export const apiDelete = async <T = any>(endpoint: string): Promise<T> => {
  const response = await apiRequest(endpoint, { method: 'DELETE' });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Ошибка запроса' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  // Если статус 204 (No Content), возвращаем пустой объект, так как тело ответа отсутствует
  if (response.status === 204) {
    return {} as T;
  }
  // Пытаемся распарсить JSON, если тело ответа пустое, возвращаем пустой объект
  try {
    const text = await response.text();
    if (!text || text.trim() === '') {
      return {} as T;
    }
    return JSON.parse(text);
  } catch {
    // Если не удалось распарсить JSON, возвращаем пустой объект
    return {} as T;
  }
};

